import { AnimalType, MedType, IndicationType } from '../types/dosage-types';

// Define interfaces for the treatment data structure
export interface TreatmentOption {
  animalType: AnimalType;
  medType: MedType;
  indicationType: IndicationType;
  animalName: string;
  medicationName: string;
  indicationName: string;
  urlSlug: string;
}

export interface AnimalOption {
  id: AnimalType;
  name: string;
  icon: string;
  description: string;
}

export interface MedicationOption {
  id: MedType;
  name: string;
  description: string;
  ailmentIds: string[];
}

export interface AilmentOption {
  id: string;
  name: string;
  description: string;
  animalIds: AnimalType[];
  medicationIds: MedType[];
}

export interface ToolOption {
  id: string;
  name: string;
  type: 'calculator' | 'chart' | 'guide';
  url: string;
  description: string;
  medicationIds: MedType[];
  indicationType: IndicationType;
}

export class TreatmentDataService {
  private static treatmentOptions: TreatmentOption[] = [];
  private static animalOptions: AnimalOption[] = [];
  private static ailmentOptions: AilmentOption[] = [];
  private static medicationOptions: MedicationOption[] = [];
  private static toolOptions: ToolOption[] = [];
  private static initialized = false;

  // Static data that maps the YAML structure
  private static initializeStaticData() {
    if (this.initialized) return;

    // Animal options with icons and descriptions
    this.animalOptions = [
      { id: AnimalType.DOG, name: 'Dog', icon: '🐕', description: 'Canine companions' },
      { id: AnimalType.CAT, name: 'Cat', icon: '🐱', description: 'Feline friends' },
      { id: AnimalType.CHICKEN, name: 'Chicken', icon: '🐔', description: 'Poultry birds' },
      { id: AnimalType.GOAT, name: 'Goat', icon: '🐐', description: 'Farm animals' },
      { id: AnimalType.HORSE, name: 'Horse', icon: '🐴', description: 'Equine animals' },
      { id: AnimalType.RABBIT, name: 'Rabbit', icon: '🐰', description: 'Small mammals' }
    ];

    // Treatment combinations based on the actual YAML data
    this.treatmentOptions = [
      // Dog treatments
      { animalType: AnimalType.DOG, medType: MedType.TOLTRAZURIL, indicationType: IndicationType.TREATMENT, animalName: 'Dog', medicationName: 'Toltrazuril', indicationName: 'Treatment', urlSlug: 'dog-toltrazuril-treatment-calculator' },
      { animalType: AnimalType.DOG, medType: MedType.FENBENDAZOLE, indicationType: IndicationType.TREATMENT, animalName: 'Dog', medicationName: 'Fenbendazole', indicationName: 'Treatment', urlSlug: 'dog-fenbendazole-treatment-calculator' },
      { animalType: AnimalType.DOG, medType: MedType.IVERMECTIN, indicationType: IndicationType.TREATMENT, animalName: 'Dog', medicationName: 'Ivermectin', indicationName: 'Treatment', urlSlug: 'dog-ivermectin-treatment-calculator' },
      { animalType: AnimalType.DOG, medType: MedType.PYRANTEL, indicationType: IndicationType.TREATMENT, animalName: 'Dog', medicationName: 'Pyrantel Pamoate', indicationName: 'Treatment', urlSlug: 'dog-pyrantel-treatment-calculator' },

      // Cat treatments
      { animalType: AnimalType.CAT, medType: MedType.TOLTRAZURIL, indicationType: IndicationType.TREATMENT, animalName: 'Cat', medicationName: 'Toltrazuril', indicationName: 'Treatment', urlSlug: 'cat-toltrazuril-treatment-calculator' },
      { animalType: AnimalType.CAT, medType: MedType.PONAZURIL, indicationType: IndicationType.TREATMENT, animalName: 'Cat', medicationName: 'Ponazuril', indicationName: 'Treatment', urlSlug: 'cat-ponazuril-treatment-calculator' },

      // Chicken treatments
      { animalType: AnimalType.CHICKEN, medType: MedType.TOLTRAZURIL, indicationType: IndicationType.TREATMENT, animalName: 'Chicken', medicationName: 'Toltrazuril', indicationName: 'Treatment', urlSlug: 'chicken-toltrazuril-treatment-calculator' },
      { animalType: AnimalType.CHICKEN, medType: MedType.AMPROLIUM, indicationType: IndicationType.TREATMENT, animalName: 'Chicken', medicationName: 'Amprolium', indicationName: 'Treatment', urlSlug: 'chicken-amprolium-treatment-calculator' },
      { animalType: AnimalType.CHICKEN, medType: MedType.SULFAMETHOXINE, indicationType: IndicationType.TREATMENT, animalName: 'Chicken', medicationName: 'Sulfamethoxine', indicationName: 'Treatment', urlSlug: 'chicken-sulfamethoxine-treatment-calculator' },

      // Goat treatments
      { animalType: AnimalType.GOAT, medType: MedType.SULFADIMETHOXINE, indicationType: IndicationType.TREATMENT, animalName: 'Goat', medicationName: 'Sulfadimethoxine', indicationName: 'Treatment', urlSlug: 'goat-sulfadimethoxine-treatment-calculator' },
      { animalType: AnimalType.GOAT, medType: MedType.IVERMECTIN, indicationType: IndicationType.TREATMENT, animalName: 'Goat', medicationName: 'Ivermectin', indicationName: 'Treatment', urlSlug: 'goat-ivermectin-treatment-calculator' },
      { animalType: AnimalType.GOAT, medType: MedType.FENBENDAZOLE, indicationType: IndicationType.TREATMENT, animalName: 'Goat', medicationName: 'Fenbendazole', indicationName: 'Treatment', urlSlug: 'goat-fenbendazole-treatment-calculator' },
      { animalType: AnimalType.GOAT, medType: MedType.PYRANTEL, indicationType: IndicationType.TREATMENT, animalName: 'Goat', medicationName: 'Pyrantel Tartrate', indicationName: 'Treatment', urlSlug: 'goat-pyrantel-treatment-calculator' },
      { animalType: AnimalType.GOAT, medType: MedType.PONAZURIL, indicationType: IndicationType.TREATMENT, animalName: 'Goat', medicationName: 'Ponazuril', indicationName: 'Treatment', urlSlug: 'goat-ponazuril-treatment-calculator' },

      // Horse treatments
      { animalType: AnimalType.HORSE, medType: MedType.TOLTRAZURIL, indicationType: IndicationType.TREATMENT, animalName: 'Horse', medicationName: 'Toltrazuril', indicationName: 'Treatment', urlSlug: 'horse-toltrazuril-treatment-calculator' },
      { animalType: AnimalType.HORSE, medType: MedType.IVERMECTIN, indicationType: IndicationType.TREATMENT, animalName: 'Horse', medicationName: 'Ivermectin', indicationName: 'Treatment', urlSlug: 'horse-ivermectin-treatment-calculator' },
      { animalType: AnimalType.HORSE, medType: MedType.FENBENDAZOLE, indicationType: IndicationType.TREATMENT, animalName: 'Horse', medicationName: 'Fenbendazole', indicationName: 'Treatment', urlSlug: 'horse-fenbendazole-treatment-calculator' },
      { animalType: AnimalType.HORSE, medType: MedType.PYRANTEL, indicationType: IndicationType.TREATMENT, animalName: 'Horse', medicationName: 'Pyrantel Pamoate', indicationName: 'Treatment', urlSlug: 'horse-pyrantel-treatment-calculator' }
    ];

    // Ailment options based on common conditions and medication mappings
    this.ailmentOptions = [
      {
        id: 'coccidiosis',
        name: 'Coccidiosis',
        description: 'Parasitic infection affecting intestines',
        animalIds: [AnimalType.DOG, AnimalType.CAT, AnimalType.CHICKEN, AnimalType.GOAT, AnimalType.HORSE],
        medicationIds: [MedType.TOLTRAZURIL, MedType.AMPROLIUM, MedType.SULFAMETHOXINE, MedType.PONAZURIL]
      },
      {
        id: 'worms',
        name: 'Intestinal Worms',
        description: 'Various parasitic worms and nematodes',
        animalIds: [AnimalType.DOG, AnimalType.GOAT, AnimalType.HORSE],
        medicationIds: [MedType.FENBENDAZOLE, MedType.IVERMECTIN, MedType.PYRANTEL]
      },
      {
        id: 'heartworm-prevention',
        name: 'Heartworm Prevention',
        description: 'Monthly prevention for heartworm infection',
        animalIds: [AnimalType.DOG],
        medicationIds: [MedType.IVERMECTIN]
      }
    ];

    // Medication options
    this.medicationOptions = [
      { id: MedType.TOLTRAZURIL, name: 'Toltrazuril', description: 'Anti-coccidial medication', ailmentIds: ['coccidiosis'] },
      { id: MedType.AMPROLIUM, name: 'Amprolium', description: 'Anti-coccidial medication for poultry', ailmentIds: ['coccidiosis'] },
      { id: MedType.SULFAMETHOXINE, name: 'Sulfamethoxine', description: 'Sulfonamide antibiotic for coccidiosis', ailmentIds: ['coccidiosis'] },
      { id: MedType.SULFADIMETHOXINE, name: 'Sulfadimethoxine (Albon)', description: 'Sulfonamide antibiotic', ailmentIds: ['coccidiosis'] },
      { id: MedType.PONAZURIL, name: 'Ponazuril', description: 'Anti-protozoal medication', ailmentIds: ['coccidiosis'] },
      { id: MedType.FENBENDAZOLE, name: 'Fenbendazole', description: 'Broad-spectrum dewormer', ailmentIds: ['worms'] },
      { id: MedType.IVERMECTIN, name: 'Ivermectin', description: 'Anti-parasitic medication', ailmentIds: ['worms', 'heartworm-prevention'] },
      { id: MedType.PYRANTEL, name: 'Pyrantel', description: 'Deworming medication', ailmentIds: ['worms'] }
    ];

    // Tool options - simplified to just calculators
    this.toolOptions = [
      { id: 'dosage-calc', name: 'Dosage Calculator', type: 'calculator', url: '', description: 'Calculate precise dosing', medicationIds: Object.values(MedType), indicationType: IndicationType.TREATMENT }
    ];

    this.initialized = true;
  }

  static getAnimals(): AnimalOption[] {
    this.initializeStaticData();
    return this.animalOptions;
  }

  static getAilmentsForAnimal(animalType: AnimalType): AilmentOption[] {
    this.initializeStaticData();
    return this.ailmentOptions.filter(ailment => ailment.animalIds.includes(animalType));
  }

  static getMedicationsForAilment(ailmentId: string): MedicationOption[] {
    this.initializeStaticData();
    const ailment = this.ailmentOptions.find(a => a.id === ailmentId);
    if (!ailment) return [];
    return this.medicationOptions.filter(med => ailment.medicationIds.includes(med.id));
  }

  static getToolsForMedication(medType: MedType): ToolOption[] {
    this.initializeStaticData();
    return this.toolOptions.filter(tool => tool.medicationIds.includes(medType));
  }

  static getTreatmentUrl(animalType: AnimalType, medType: MedType, indicationType: IndicationType): string {
    this.initializeStaticData();
    const treatment = this.treatmentOptions.find(t =>
      t.animalType === animalType &&
      t.medType === medType &&
      t.indicationType === indicationType
    );
    return treatment ? `/${treatment.urlSlug}` : '';
  }

  static getAvailableTreatments(): TreatmentOption[] {
    this.initializeStaticData();
    return this.treatmentOptions;
  }
}